import React, { useEffect, useMemo, useRef, useState } from "react";
import { InfoWindow, Polygon } from "@react-google-maps/api";
import { Empty, Select, Spin } from "antd";
import { Filter, Layers3, MapPinned, RotateCcw } from "lucide-react";
import { useQuery } from "react-query";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import NyayPanchayatAnalyticsPanel from "./NyayPanchayatAnalyticsPanel";

const toComparable = (value = "") => value?.toString().trim().toUpperCase();

const getCenter = (path = []) => {
  if (!window.google || !path.length) return null;

  const bounds = new window.google.maps.LatLngBounds();
  path.forEach((point) => bounds.extend(point));
  return bounds.getCenter();
};

const NyayePanchayatMap = ({ polygonPaths = [], mapRef }) => {
  const axiosInstance = useAxiosInstance();
  const initialBoundsRef = useRef(null);

  const [hoveredBlockName, setHoveredBlockName] = useState(null);
  const [hoveredBlockCenter, setHoveredBlockCenter] = useState(null);
  const [filters, setFilters] = useState({
    nyayPanchayat: "",
    village: "",
  });

  useEffect(() => {
    if (!window.google || !polygonPaths.length || initialBoundsRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    polygonPaths.forEach((poly) => poly.path.forEach((point) => bounds.extend(point)));

    if (!bounds.isEmpty()) {
      initialBoundsRef.current = bounds;
    }
  }, [polygonPaths]);

  const { data: nyayPanchayats = [], isLoading: isNyayPanchayatLoading } = useQuery(
    ["nyay-panchayats"],
    async () => {
      const res = await axiosInstance.get("/api/nyayePanchayat/nyayPanchayats");
      return res?.data?.data || [];
    },
    {
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const {
    data: analytics,
    isLoading: isAnalyticsLoading,
  } = useQuery(
    ["nyay-panchayat-analytics", filters.nyayPanchayat, filters.village],
    async () => {
      const res = await axiosInstance.get("/api/nyayePanchayat/analytics", {
        params: {
          nyayPanchayat: filters.nyayPanchayat || undefined,
          village: filters.village || undefined,
        },
      });

      return res?.data?.data || null;
    },
    {
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const {
    data: nyayPanchayatVillageResponse,
    isLoading: isNyayPanchayatVillageLoading,
  } = useQuery(
    ["nyay-panchayat-villages", filters.nyayPanchayat],
    async () => {
      const res = await axiosInstance.get(
        "/api/nyayePanchayat/villagesNyayPanchayatWise",
        {
          params: {
            nyayPanchayat: filters.nyayPanchayat,
          },
        }
      );

      return res?.data?.data?.villages || [];
    },
    {
      enabled: !!filters.nyayPanchayat,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const blockMetadata = useMemo(() => {
    const seen = new Map();

    polygonPaths.forEach((poly) => {
      if (poly.type !== "block" || !poly.name || seen.has(poly.name)) return;
      seen.set(poly.name, {
        name: poly.name,
        color: poly.color || "#C7E0F4",
      });
    });

    return Array.from(seen.values());
  }, [polygonPaths]);

  const villageEntries = useMemo(() => {
    const seen = new Map();

    polygonPaths.forEach((poly) => {
      if (poly.type !== "village" || !poly.name) return;

      const key = toComparable(poly.name);
      if (seen.has(key)) return;

      seen.set(key, {
        name: poly.name,
        blockName: poly.blockName || "",
        nyayPanchayat: poly.nyayPanchayat || "",
      });
    });

    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [polygonPaths]);

  const blockVillageCounts = useMemo(() => {
    return villageEntries.reduce((accumulator, item) => {
      if (!item.blockName) return accumulator;
      accumulator[item.blockName] = (accumulator[item.blockName] || 0) + 1;
      return accumulator;
    }, {});
  }, [villageEntries]);

  const villagesForSelectedNP = useMemo(() => {
    if (!filters.nyayPanchayat) return villageEntries;

    const apiVillages = nyayPanchayatVillageResponse || [];

    if (!apiVillages.length) {
      return villageEntries.filter(
        (item) =>
          toComparable(item.nyayPanchayat) === toComparable(filters.nyayPanchayat)
      );
    }

    return apiVillages
      .map((villageName) => {
        const localVillage = villageEntries.find(
          (item) => toComparable(item.name) === toComparable(villageName)
        );

        return (
          localVillage || {
            name: villageName,
            blockName: "",
            nyayPanchayat: filters.nyayPanchayat,
          }
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filters.nyayPanchayat, nyayPanchayatVillageResponse, villageEntries]);

  const highlightedVillages = useMemo(() => {
    if (filters.village) {
      return [filters.village];
    }

    if (!filters.nyayPanchayat) {
      return [];
    }

    const apiVillages = nyayPanchayatVillageResponse || [];

    if (apiVillages.length) {
      return apiVillages;
    }

    return villageEntries
      .filter(
        (item) =>
          toComparable(item.nyayPanchayat) === toComparable(filters.nyayPanchayat)
      )
      .map((item) => item.name);
  }, [
    filters.nyayPanchayat,
    filters.village,
    nyayPanchayatVillageResponse,
    villageEntries,
  ]);

  const selectedVillagePolygons = useMemo(() => {
    if (!filters.village) return [];

    return polygonPaths.filter(
      (poly) =>
        poly.type === "village" &&
        toComparable(poly.name) === toComparable(filters.village)
    );
  }, [filters.village, polygonPaths]);

  const selectedVillageMeta = useMemo(() => {
    if (selectedVillagePolygons.length) {
      return selectedVillagePolygons[0];
    }

    return villageEntries.find(
      (item) => toComparable(item.name) === toComparable(filters.village)
    );
  }, [filters.village, selectedVillagePolygons, villageEntries]);

  const zoomToPolygons = (items = [], padding = 80) => {
    if (!mapRef?.current || !window.google || !items.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    items.forEach((poly) => poly.path.forEach((point) => bounds.extend(point)));

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, padding);
    }
  };

  const resetView = () => {
    setFilters({ nyayPanchayat: "", village: "" });

    if (mapRef?.current && initialBoundsRef.current) {
      mapRef.current.fitBounds(initialBoundsRef.current);
    }
  };

  const handleNyayPanchayatSelect = (value) => {
    if (!value) {
      resetView();
      return;
    }

    setFilters({
      nyayPanchayat: value,
      village: "",
    });
  };

  useEffect(() => {
    if (!filters.nyayPanchayat || !highlightedVillages.length) return;

    const targetPolygons = polygonPaths.filter(
      (poly) =>
        poly.type === "village" &&
        highlightedVillages.some(
          (villageName) => toComparable(villageName) === toComparable(poly.name)
        )
    );

    zoomToPolygons(targetPolygons);
  }, [filters.nyayPanchayat, highlightedVillages, polygonPaths]);

  const handleVillageSelect = (value) => {
    if (!value) {
      setFilters((previous) => ({
        ...previous,
        village: "",
      }));

      if (filters.nyayPanchayat) {
        handleNyayPanchayatSelect(filters.nyayPanchayat);
      } else if (mapRef?.current && initialBoundsRef.current) {
        mapRef.current.fitBounds(initialBoundsRef.current);
      }

      return;
    }

    const matchingPolygons = polygonPaths.filter(
      (poly) => poly.type === "village" && toComparable(poly.name) === toComparable(value)
    );
    const matchingVillage = villageEntries.find(
      (item) => toComparable(item.name) === toComparable(value)
    );

    setFilters({
      nyayPanchayat: matchingVillage?.nyayPanchayat || filters.nyayPanchayat || "",
      village: matchingVillage?.name || value,
    });

    zoomToPolygons(matchingPolygons);
  };

  const handleBlockClick = (blockName) => {
    const targetPolygons = polygonPaths.filter(
      (poly) => poly.type === "block" && toComparable(poly.name) === toComparable(blockName)
    );
    zoomToPolygons(targetPolygons, 50);
  };

  const infoWindowCenter = useMemo(() => {
    if (!selectedVillagePolygons.length || !window.google) return null;

    const bounds = new window.google.maps.LatLngBounds();
    selectedVillagePolygons.forEach((poly) =>
      poly.path.forEach((point) => bounds.extend(point))
    );

    return bounds.isEmpty() ? null : bounds.getCenter();
  }, [selectedVillagePolygons]);

  if (isNyayPanchayatLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {polygonPaths.map((poly, index) => {
        const isHoveredBlock =
          poly.type === "block" && hoveredBlockName === poly.name;
        const isSelectedVillage =
          poly.type === "village" &&
          filters.village &&
          toComparable(filters.village) === toComparable(poly.name);
        const isHighlightedVillage =
          poly.type === "village" &&
          highlightedVillages.some(
            (item) => toComparable(item) === toComparable(poly.name)
          );
        const isVillageInsideSelectedNP =
          poly.type === "village" &&
          filters.nyayPanchayat &&
          toComparable(poly.nyayPanchayat) === toComparable(filters.nyayPanchayat);

        let strokeColor = "#334155";
        let strokeWeight = 1;
        let fillColor = "transparent";
        let fillOpacity = 0;
        let strokeOpacity = 0.35;

        if (poly.type === "block") {
          fillColor = poly.color || "#C7E0F4";
          strokeColor = poly.color || "#7AB8D9";

          if (filters.nyayPanchayat) {
            fillOpacity = 0.05;
            strokeOpacity = isHoveredBlock ? 0.45 : 0.2;
            strokeWeight = isHoveredBlock ? 2 : 1;
          } else {
            fillOpacity = isHoveredBlock ? 0.88 : 0.68;
            strokeOpacity = 1;
            strokeWeight = isHoveredBlock ? 2.5 : 1.4;
          }
        }

        // if (poly.type === "village") {
        //   if (filters.nyayPanchayat) {
        //     if (isVillageInsideSelectedNP) {
        //       fillColor = isSelectedVillage ? "#0F766E" : "#FFB703";
        //       fillOpacity = isSelectedVillage ? 0.72 : 0.48;
        //       strokeColor = isSelectedVillage ? "#134E4A" : "#9A6700";
        //       strokeOpacity = 1;
        //       strokeWeight = isSelectedVillage ? 3.2 : 2;
        //     } else {
        //       fillColor = "transparent";
        //       fillOpacity = 0;
        //       strokeColor = "transparent";
        //       strokeOpacity = 0;
        //       strokeWeight = 0;
        //     }
        //   } else if (isSelectedVillage) {
        //     fillColor = "#16608D";
        //     fillOpacity = 0.42;
        //     strokeColor = "#0F3B63";
        //     strokeOpacity = 1;
        //     strokeWeight = 3;
        //   } else {
        //     fillColor = "transparent";
        //     fillOpacity = isHighlightedVillage ? 0.18 : 0.02;
        //     strokeColor = isHighlightedVillage ? "#16608D" : "#475569";
        //     strokeOpacity = isHighlightedVillage ? 0.8 : 0.22;
        //     strokeWeight = isHighlightedVillage ? 1.8 : 0.9;
        //   }
        // }

        if (poly.type === "village") {
  const isHighlighted = highlightedVillages.some(
    (v) => toComparable(v) === toComparable(poly.name)
  );

  if (filters.nyayPanchayat) {
    if (isHighlighted) {
      // ✅ YELLOW HIGHLIGHT (clean + visible)
      fillColor = "#FFD54F";       // softer yellow
      fillOpacity = 0.7;

      strokeColor = "#F57F17";     // darker border
      strokeWeight = 2;
      strokeOpacity = 1;

      poly.zIndexOverride = 999;
    } else {
      // hide other villages
      fillOpacity = 0;
      strokeOpacity = 0;
      strokeWeight = 0;
      fillColor = "transparent";
    }
  } else {
    // default behavior
    fillColor = isSelectedVillage ? "#16608D" : "transparent";
    fillOpacity = isSelectedVillage ? 0.4 : 0;

    strokeColor = isSelectedVillage ? "#0F3B63" : "#475569";
    strokeWeight = isSelectedVillage ? 2.5 : 0.8;
    strokeOpacity = isSelectedVillage ? 1 : 0.3;
  }
}

        if (poly.type === "constituency") {
          strokeColor = "#0F3B63";
          strokeOpacity = 1;
          strokeWeight = 2.5;
        }

        return (
          <Polygon
            key={`${poly.type}-${poly.name}-${index}`}
            paths={poly.path}
            options={{
              strokeColor,
              strokeWeight,
              strokeOpacity,
              fillColor,
              fillOpacity,
              clickable: poly.type === "block" || poly.type === "village",
              zIndex:
                poly.type === "constituency"
                  ? 1000
                  : poly.type === "village"
                  ? isSelectedVillage
                    ? 900
                    : 200
                  : 100,
            }}
            onMouseOver={() => {
              if (poly.type !== "block") return;
              setHoveredBlockName(poly.name);
              setHoveredBlockCenter(getCenter(poly.path));
            }}
            onMouseOut={() => {
              if (poly.type !== "block") return;
              setHoveredBlockName(null);
              setHoveredBlockCenter(null);
            }}
            onClick={() => {
              if (poly.type === "block") {
                handleBlockClick(poly.name);
                return;
              }

              if (poly.type === "village") {
                handleVillageSelect(poly.name);
              }
            }}
          />
        );
      })}

      {hoveredBlockName && hoveredBlockCenter ? (
        <InfoWindow position={hoveredBlockCenter}>
          <div className="min-w-[180px] rounded-2xl bg-white/95 p-3 text-black shadow-xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Block</p>
            <h3 className="mt-1 text-base font-semibold text-[#123865]">{hoveredBlockName}</h3>
            <p className="mt-2 text-xs text-slate-600">
              {blockVillageCounts[hoveredBlockName] || 0} villages mapped in this block
            </p>
            <p className="mt-2 text-xs font-medium text-[#16608D]">Click block to zoom in</p>
          </div>
        </InfoWindow>
      ) : null}

      {selectedVillageMeta && infoWindowCenter ? (
        <InfoWindow
          position={infoWindowCenter}
          onCloseClick={() =>
            setFilters((previous) => ({
              ...previous,
              village: "",
            }))
          }
        >
          <div className="min-w-[260px] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.22)]">
            <div className="bg-[linear-gradient(135deg,#0F3B63_0%,#16608D_100%)] px-4 py-3 text-white">
              <p className="text-xs uppercase tracking-[0.25em] text-white/70">Village Snapshot</p>
              <h3 className="mt-1 text-lg font-semibold">{selectedVillageMeta.name}</h3>
              <p className="mt-1 text-xs text-white/80">
                {selectedVillageMeta.nyayPanchayat || "Nyay Panchayat N/A"} •{" "}
                {selectedVillageMeta.blockName || "Block N/A"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 px-4 py-4 text-black">
              <div className="rounded-2xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Party</p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {analytics?.selectedVillageRecord?.party || "-"}
                </p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Votes</p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {analytics?.selectedVillageRecord?.votes ?? "-"}
                </p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Vote Share</p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {analytics?.selectedVillageRecord?.voteSharePct ?? 0}%
                </p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Rank</p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {analytics?.selectedVillageRecord?.rank || "-"}
                </p>
              </div>
            </div>
            <div className="border-t border-slate-100 px-4 py-3 text-xs text-slate-600">
              Category:{" "}
              <span className="font-semibold text-[#123865]">
                {analytics?.selectedVillageRecord?.category || "-"}
              </span>
            </div>
          </div>
        </InfoWindow>
      ) : null}

      <div className="absolute left-4 top-4 z-[2300] w-[330px] max-w-[calc(100vw-2rem)] rounded-[24px] border border-white/30 bg-white/78 p-4 shadow-[0_24px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl">
        <div className="mb-3 flex items-center gap-2 text-[#123865]">
          <Filter size={18} />
          <span className="text-sm font-semibold">Nyay Panchayat Filters</span>
        </div>

        <div className="space-y-3">
          <Select
            placeholder="Select Nyay Panchayat"
            value={filters.nyayPanchayat || undefined}
            onChange={handleNyayPanchayatSelect}
            options={nyayPanchayats.map((item) => ({
              label: item,
              value: item,
            }))}
            loading={isNyayPanchayatLoading}
            allowClear
            showSearch
            getPopupContainer={(trigger) => trigger.parentNode}
            style={{ width: "100%" }}
          />

          <Select
            placeholder="Select Village"
            value={filters.village || undefined}
            onChange={handleVillageSelect}
            options={villagesForSelectedNP.map((item) => ({
              label: item.name,
              value: item.name,
            }))}
            loading={isNyayPanchayatVillageLoading}
            allowClear
            showSearch
            getPopupContainer={(trigger) => trigger.parentNode}
            notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No villages" />}
            style={{ width: "100%" }}
          />

          <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <Layers3 size={15} className="text-[#16608D]" />
              <span>{blockMetadata.length} blocks</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPinned size={15} className="text-[#16608D]" />
              <span>{highlightedVillages.length || villageEntries.length} villages in view</span>
            </div>
          </div>

          <button
            type="button"
            onClick={resetView}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#123865] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#0F3B63]"
          >
            <RotateCcw size={16} />
            Reset Map Filters
          </button>
        </div>
      </div>

      {isAnalyticsLoading ? (
        <div className="absolute right-4 top-[12%] z-[2200] flex h-[220px] w-[420px] items-center justify-center rounded-[28px] border border-white/30 bg-white/70 shadow-[0_24px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl">
          <Spin size="large" />
        </div>
      ) : (
        <NyayPanchayatAnalyticsPanel
          analytics={analytics}
          filters={filters}
          onVillageSelect={handleVillageSelect}
          onNyayPanchayatSelect={handleNyayPanchayatSelect}
        />
      )}

      <div className="absolute bottom-5 left-4 z-[2200] max-h-[42vh] w-[290px] overflow-y-auto rounded-[24px] border border-white/30 bg-white/76 p-4 shadow-[0_24px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Blocks Legend</p>
          </div>
          {filters.nyayPanchayat ? (
            <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-800">
              Block fills muted
            </span>
          ) : null}
        </div>

        <div className="space-y-2">
          {blockMetadata.map((block) => (
            <button
              key={block.name}
              type="button"
              onClick={() => handleBlockClick(block.name)}
              className="flex w-full items-center justify-between rounded-2xl border border-slate-100 px-3 py-2 text-left transition hover:border-[#78C5E7] hover:bg-[#EFF8FD]"
            >
              <div className="flex items-center gap-3">
                <span
                  className="h-4 w-4 rounded-md border border-slate-200"
                  style={{
                    backgroundColor: filters.nyayPanchayat ? "transparent" : block.color,
                    borderColor: block.color,
                  }}
                />
                <span className="text-sm font-medium text-slate-700">{block.name}</span>
              </div>
              <span className="text-xs text-slate-500">
                {blockVillageCounts[block.name] || 0}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default NyayePanchayatMap;
